import type { LobbyEvent, LobbyEventType, NowPlayingRef, PlaybackState } from '@/types/lobby';

type WebSocketCtor = new (url: string, protocols?: string | string[]) => WebSocket;

/**
 * WebSocket subprotocol marker — must match the backend's `WS_BEARER_PROTOCOL`
 * in `cobstream-backend/src/services/lobby/lobby.ws.ts`. The client passes
 * the JWT as the second subprotocol value so it travels in the
 * `Sec-WebSocket-Protocol` request header (not in the URL), keeping it out
 * of access logs, Referer headers, and browser history.
 */
export const WS_BEARER_PROTOCOL = 'cobstream-bearer';

type Handler<E extends LobbyEvent = LobbyEvent> = (event: E) => void;
type AnyHandler = (event: LobbyEvent) => void;

/**
 * Resolves the WS URL for a lobby from `VITE_API_URL`. Works both in local
 * dev (where `VITE_API_URL` is typically empty -> same-origin) and in
 * deployed environments (where it points at the backend URL).
 *
 * Substitutes the scheme: `http://` -> `ws://`, `https://` -> `wss://`.
 * When no API base is configured we fall back to the current window's
 * origin so dev preview / `vite` proxy setups still work.
 *
 * The auth token does NOT appear in the URL — it's sent as a
 * `Sec-WebSocket-Protocol` subprotocol value via `wsAuthProtocols()`.
 */
export function buildLobbyWsUrl(lobbyId: string): string {
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  let base = apiBase.trim();
  if (!base) {
    if (typeof window === 'undefined') {
      base = 'ws://localhost';
    } else {
      const loc = window.location;
      const wsScheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
      base = `${wsScheme}//${loc.host}`;
    }
  } else if (base.startsWith('https://')) {
    base = `wss://${base.slice('https://'.length)}`;
  } else if (base.startsWith('http://')) {
    base = `ws://${base.slice('http://'.length)}`;
  }
  // Strip a trailing slash so we don't end up with `//api/lobby/...`.
  if (base.endsWith('/')) base = base.slice(0, -1);
  return `${base}/api/lobby/${encodeURIComponent(lobbyId)}/ws`;
}

/**
 * Builds the `protocols` array passed to `new WebSocket(url, protocols)`.
 * The browser joins these with commas in the `Sec-WebSocket-Protocol`
 * request header. The server reads the second value as the bearer token
 * and echoes back only the marker.
 */
export function wsAuthProtocols(token: string): [string, string] {
  return [WS_BEARER_PROTOCOL, token];
}

export interface LobbyWsClient {
  connect(lobbyId: string, token: string): void;
  disconnect(code?: number, reason?: string): void;
  sendChat(text: string): void;
  sendPlay(playTimestamp: number): void;
  sendPause(playTimestamp: number): void;
  sendSeek(playTimestamp: number): void;
  sendHeartbeat(playTimestamp: number, state?: PlaybackState): void;
  sendNowPlaying(ref: NowPlayingRef, source: string | null): void;
  on(eventType: LobbyEventType | '*', handler: Handler): () => void;
  /** Lifecycle hooks for the slice to observe socket state. */
  onOpen(handler: () => void): () => void;
  onClose(handler: (info: { code: number; reason: string }) => void): () => void;
  /** Expose readyState for tests / health checks. */
  getReadyState(): number;
}

export interface LobbyWsClientOptions {
  /** Optional injection for tests. Defaults to the global WebSocket. */
  webSocketCtor?: WebSocketCtor;
}

/**
 * Thin WebSocket wrapper that the lobby slice + chat panel consume.
 *
 * Intentionally minimal — no automatic reconnect logic here; the slice owns
 * reconnect with exponential backoff so it can update Redux state between
 * attempts. Send methods for play/pause/seek are intentionally omitted
 * (YAGNI — that's Phase 2 work per BACKLOG.md).
 */
export function createLobbyWsClient(opts: LobbyWsClientOptions = {}): LobbyWsClient {
  const Ctor: WebSocketCtor = opts.webSocketCtor
    ?? (typeof WebSocket !== 'undefined' ? WebSocket : (undefined as unknown as WebSocketCtor));
  if (!Ctor) {
    throw new Error('No WebSocket implementation available');
  }

  let socket: WebSocket | null = null;
  const handlers: Map<LobbyEventType | '*', Set<AnyHandler>> = new Map();
  const openHandlers: Set<() => void> = new Set();
  const closeHandlers: Set<(info: { code: number; reason: string }) => void> = new Set();

  function emit(event: LobbyEvent): void {
    const typed = handlers.get(event.type);
    if (typed) typed.forEach((h) => safeCall(() => h(event)));
    const wild = handlers.get('*');
    if (wild) wild.forEach((h) => safeCall(() => h(event)));
  }

  function safeCall(fn: () => void): void {
    try { fn(); } catch (err) {
      // Swallow handler errors so one bad listener doesn't kill the socket.
      // eslint-disable-next-line no-console
      console.error('[lobbyWs] handler threw:', err);
    }
  }

  return {
    connect(lobbyId, token) {
      // If a socket already exists, close it first so we don't leak.
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        try { socket.close(1000, 'reconnecting'); } catch { /* ignore */ }
      }
      const url = buildLobbyWsUrl(lobbyId);
      socket = new Ctor(url, wsAuthProtocols(token));
      socket.onopen = () => {
        openHandlers.forEach((h) => safeCall(h));
      };
      socket.onmessage = (msg: MessageEvent<string>) => {
        let parsed: unknown;
        try { parsed = JSON.parse(typeof msg.data === 'string' ? msg.data : String(msg.data)); }
        catch { return; }
        if (!parsed || typeof parsed !== 'object' || typeof (parsed as { type?: unknown }).type !== 'string') {
          return;
        }
        emit(parsed as LobbyEvent);
      };
      socket.onclose = (ev: CloseEvent) => {
        closeHandlers.forEach((h) => safeCall(() => h({ code: ev.code, reason: ev.reason })));
      };
      socket.onerror = () => {
        // We rely on the subsequent `onclose` to surface the failure to listeners.
      };
    },
    disconnect(code = 1000, reason = 'client_leave') {
      if (!socket) return;
      try { socket.close(code, reason); } catch { /* ignore */ }
      socket = null;
    },
    sendChat(text) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'chat', text }));
    },
    sendPlay(playTimestamp) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'play', playTimestamp }));
    },
    sendPause(playTimestamp) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'pause', playTimestamp }));
    },
    sendSeek(playTimestamp) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'seek', playTimestamp }));
    },
    sendHeartbeat(playTimestamp, state) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      // Omit the field entirely when 'playing' so we stay byte-equivalent to
      // the pre-protocol-extension client (smaller payload + zero behavior
      // change on older backends that ignore unknown fields).
      const payload = state && state !== 'playing'
        ? { type: 'heartbeat', playTimestamp, state }
        : { type: 'heartbeat', playTimestamp };
      socket.send(JSON.stringify(payload));
    },
    sendNowPlaying(ref, source) {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify({ type: 'now_playing', ref, source }));
    },
    on(eventType, handler) {
      let set = handlers.get(eventType);
      if (!set) {
        set = new Set();
        handlers.set(eventType, set);
      }
      set.add(handler as AnyHandler);
      return () => { set?.delete(handler as AnyHandler); };
    },
    onOpen(handler) {
      openHandlers.add(handler);
      return () => { openHandlers.delete(handler); };
    },
    onClose(handler) {
      closeHandlers.add(handler);
      return () => { closeHandlers.delete(handler); };
    },
    getReadyState() {
      return socket?.readyState ?? WebSocket.CLOSED;
    },
  };
}

/** Shared singleton — one socket per browser tab is enough. */
export const lobbyWsClient = createLobbyWsClient();
